import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setNodes } from "../../reducers/nodes";
import FileManager from "../../service/FileManager/FileManager";
import { FileManagerFile, StorageProject } from "../../service/FileManager/FileManagerFile";
import { FILE_MANAGER_PAGE_SIZES, normalizeStorageProject } from "../../service/FileManager/utils";
import { getMeQrStorageSize, IMeQrStorageData, updateMeQrStorage } from "../../service/MeQrStorageService";
import { useGetPageLazyQuery } from "../../service/Page/PageService.hooks";
import { RootState } from "../../store";
import {
    useDeleteFileMutation,
    useGetUserFilesPageQuery,
    usePrepareFileForPageMutation,
    useUploadFileMutation
} from "./FileUploadComponent.hooks";

interface InputProperties {
    allowedFileTypes?: readonly string[]
    onFileChoose?: (file: FileManagerFile) => void;
    onClose: () => void;
}

const FileUploadComponent: React.FC<InputProperties> = ({allowedFileTypes, onFileChoose, onClose}) => {
    const user = useSelector((state: RootState) => state.appStorage.user);
    const pageId = useSelector((state: RootState) => state.appStorage.pageId);
    const isIframeOnMeQr = useSelector((state: RootState) => state.appStorage.isIframeOnMeQr);
    const dispatch = useDispatch();
    const [files, setFiles] = useState<FileManagerFile[]>([]);
    const contextProject: StorageProject = isIframeOnMeQr ? "me-qr" : "me-page";
    const [activeProject, setActiveProject] = useState<StorageProject>(contextProject);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(FILE_MANAGER_PAGE_SIZES[0]);
    const [fileOperationError, setFileOperationError] = useState<string | null>(null);
    const [meQrStorageData, setMeQrStorageData] = useState<IMeQrStorageData | null>(null);
    const [meQrStorageLoading, setMeQrStorageLoading] = useState(false);
    const [meQrStorageError, setMeQrStorageError] = useState<"load" | "refresh" | null>(null);

    const [uploadFileMutation, {loading: fileUploadLoading}] = useUploadFileMutation({});
    const [deleteFileMutation] = useDeleteFileMutation({});
    const [prepareFileForPageMutation, {loading: prepareFileLoading}] = usePrepareFileForPageMutation({});
    const {data, previousData, loading, error: filesError, refetch} = useGetUserFilesPageQuery({
        variables: {project: activeProject, page: currentPage, pageSize},
        fetchPolicy: 'network-only',
    });
    const queryData = data ?? previousData;
    const [getPage] = useGetPageLazyQuery({
        variables: {id: pageId}, 
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const contentString = data.page.contentString;
            if (contentString && JSON.parse(contentString).length > 0) {
                dispatch(setNodes({nodes: JSON.parse(contentString)}));
            }
        },
    })
    const [getPageOwner] = useGetPageLazyQuery({fetchPolicy: 'network-only'});

    const pageStorageUsed = Number(queryData?.user?.userStorage?.size ?? 0);
    const meQrUserName = queryData?.user?.email ?? user?.userName;
    const mePageFilesCount = queryData?.mePageFiles.totalCount ?? 0;
    const meQrFilesCount = queryData?.meQrFiles.totalCount ?? 0;
    const shouldLoadMeQrStorage = isIframeOnMeQr || meQrFilesCount > 0;
    const projectCounts: Record<StorageProject, number> = {
        "me-page": mePageFilesCount,
        "me-qr": meQrFilesCount,
    };
    const activeProjectFilesCount = activeProject === "me-page" ? mePageFilesCount : meQrFilesCount;
    const fallbackProjectFilesCount = activeProject === "me-page" ? meQrFilesCount : mePageFilesCount;
    const totalPages = Math.max(1, queryData?.userFilesPage.totalPages ?? 1);

    const onProjectChange = (project: StorageProject) => {
        setActiveProject(project);
        setCurrentPage(1);
    };

    const onPageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const getPageOwnerUserName = async () => {
        if (!pageId) return meQrUserName;
        const pageResult = await getPageOwner({variables: {id: Number(pageId)}});
        return pageResult.data?.page?.user?.email ?? pageResult.data?.page?.user?.userName ?? meQrUserName;
    };

    const loadMeQrStorage = useCallback(async (userName: string): Promise<boolean> => {
        if (!userName) return false;

        setMeQrStorageLoading(true);
        const storageData = await getMeQrStorageSize(userName);
        setMeQrStorageLoading(false);

        if (!storageData) {
            setMeQrStorageError("load");
            return false;
        }

        setMeQrStorageData(storageData);
        setMeQrStorageError(null);
        return true;
    }, []);

    const refreshMeQrStorage = async (updateContribution = false, userName = meQrUserName) => {
        if (!userName) return;
        const contributionUpdated = !updateContribution || await updateMeQrStorage(userName);

        if (!contributionUpdated) {
            await loadMeQrStorage(userName);
            setMeQrStorageError("refresh");
            return;
        }

        if (updateContribution) {
            await new Promise(resolve => window.setTimeout(resolve, 1000));
        }

        await loadMeQrStorage(userName);
    };

    const onFileUpload = (file: File) => {
        setFileOperationError(null);
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        let mimeType = file.type;

        if (!mimeType && ext === 'heic') {
            mimeType = 'image/heic';
        }

        const fixedName = file.name.replace(/\.[^/.]+$/, `.${ext}`);
        const fixedFile = new File([file], fixedName, { type: mimeType || file.type });

        const reader = new FileReader();
        reader.readAsDataURL(fixedFile);
        reader.onload = async () => {
            try {
                const {data} = await uploadFileMutation({
                    variables: {
                        file: reader.result as string,
                        fileName: file.name,
                        fromDomain: isIframeOnMeQr ? "me-qr" : "me-page",
                        pageId: pageId ? Number(pageId) : null,
                    },
                });
                const uploadProject = normalizeStorageProject(data?.uploadFile?.domain);
                setActiveProject(uploadProject);
                setCurrentPage(1);
                await refetch({project: uploadProject, page: 1, pageSize});
                if (uploadProject === "me-qr") {
                    await refreshMeQrStorage(true, await getPageOwnerUserName());
                }
            } catch (error) {
                setFileOperationError(error instanceof Error ? error.message : 'Error uploading file');
                console.error('Error uploading file', error);
            }
        };
    }
    const onFileDelete = async (file: FileManagerFile) => {
        try {
            await deleteFileMutation({
                variables: {id: file.id},
            });
            await Promise.all([refetch(), pageId ? getPage() : Promise.resolve()]);
            if (file.storageProject === "me-qr") await refreshMeQrStorage(true);
        } catch (error) {
            setFileOperationError(error instanceof Error ? error.message : 'Error deleting file');
            console.error('Error deleting file', error);
        }
    }

    function getFileExtension(url: string) {
        // Use the lastIndexOf method to find the position of the last dot in the URL
        const dotIndex = url.lastIndexOf('.');

        // Check if the dot is found and is not at the end of the string
        if (dotIndex !== -1 && dotIndex < url.length - 1) {
            // Return the substring from the dot to the end of the string
            return url.substring(dotIndex + 1);
        } else {
            // Return an empty string if no valid extension is found
            return '';
        }
    }

    function getFileId(filePath: string): string {
        const dotIndex = filePath.lastIndexOf('.');
        return dotIndex > 0 ? filePath.substring(0, dotIndex) : filePath;
    }

    const onPreparedFileChoose = async (file: FileManagerFile) => {
        if (!onFileChoose) return;
        if (!pageId) {
            onFileChoose(file);
            return;
        }

        setFileOperationError(null);
        try {
            const pageResult = await getPageOwner({variables: {id: Number(pageId)}});
            const pageOwnerId = pageResult.data?.page?.user?.id;

            if (pageOwnerId && pageOwnerId === queryData?.user?.id) {
                onFileChoose(file);
                return;
            }

            const {data: preparedData} = await prepareFileForPageMutation({
                variables: {pageId: Number(pageId), fileId: file.id},
            });
            const preparedFile = preparedData?.prepareFileForPage;
            if (!preparedFile?.id) throw new Error('Failed to prepare file for page');

            const ownerId = preparedFile.fullFilePath.match(/\/constructor\/user\/(\d+)\//)?.[1] ?? queryData?.user?.id;
            const serverPath = preparedFile.extension.toLowerCase() === "pdf"
                ? `${process.env.REACT_APP_BACKEND_ADDRESS}/mobile/pdf/${ownerId}/${getFileId(preparedFile.filePath)}`
                : `${process.env.REACT_APP_IMAGE_ADDRESS}${preparedFile.fullFilePath}`;

            onFileChoose({
                id: preparedFile.id,
                serverPath,
                extension: preparedFile.extension,
                title: preparedFile.fileName,
                size: preparedFile.size ?? 0,
                storageProject: normalizeStorageProject(preparedFile.domain),
            });
            await refetch();
            if (preparedFile.domain === "me-qr") {
                await refreshMeQrStorage(true, await getPageOwnerUserName());
            }
        } catch (error) {
            setFileOperationError(error instanceof Error ? error.message : 'Error preparing file for page');
            throw error;
        }
    };

    const toFileManagerFile = (file: { id?: number | null, extension: string, fileName: string, filePath: string, size?: number | null, domain?: string | null }, ownerId?: number | null): FileManagerFile => {
        const extension = file.extension || getFileExtension(file.filePath);
        const serverPath = extension.toLowerCase() === "pdf"
            ? `${process.env.REACT_APP_BACKEND_ADDRESS}/mobile/pdf/${ownerId}/${getFileId(file.filePath)}`
            : `${process.env.REACT_APP_IMAGE_ADDRESS}/constructor/user/${ownerId}/${file.filePath}`;

        return {
            id: file.id ?? 0,
            serverPath,
            extension,
            title: file.fileName,
            size: file.size ?? 0,
            storageProject: normalizeStorageProject(file.domain)
        };
    };

    useEffect(() => {
        if (!loading && data?.user) {
            const updatedFiles = data.userFilesPage.files.map(file => toFileManagerFile(file, data.user.id));
            setFiles(updatedFiles);
        }
    }, [data, loading])

    useEffect(() => {
        setActiveProject(contextProject);
        setCurrentPage(1);
    }, [contextProject]);

    useEffect(() => {
        if (loading) return;

        if (activeProjectFilesCount === 0) {
            const fallbackProject: StorageProject = activeProject === "me-page" ? "me-qr" : "me-page";
            if (fallbackProjectFilesCount > 0) onProjectChange(fallbackProject);
        }
    }, [activeProject, activeProjectFilesCount, fallbackProjectFilesCount, loading]);

    useEffect(() => {
        if (!data?.userFilesPage) return;
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, data?.userFilesPage, totalPages]);

    useEffect(() => {
        if (shouldLoadMeQrStorage && meQrUserName) {
            void loadMeQrStorage(meQrUserName);
        }
    }, [loadMeQrStorage, shouldLoadMeQrStorage, meQrUserName]);

    return <>
        {/*{openButton()}*/}
        <FileManager onModalClose={onClose} onFileChoose={onPreparedFileChoose} onFileDelete={onFileDelete} files={files}
                     allowedFileTypes={allowedFileTypes}
                     fileUploadLoading={fileUploadLoading || prepareFileLoading}
                     filesLoading={loading}
                     filesError={filesError?.message ?? null}
                     onFileUpload={onFileUpload} fileOperationError={fileOperationError} isUserPremium={!!queryData?.user?.activePremium}
                     meQrStorageData={meQrStorageData}
                     meQrStorageLoading={meQrStorageLoading}
                     meQrStorageError={meQrStorageError}
                     onRetryMeQrStorage={() => refreshMeQrStorage(true)}
                     pageStorageUsed={pageStorageUsed}
                     uploadProject={contextProject}
                     activeProject={activeProject}
                     projectCounts={projectCounts}
                     currentPage={currentPage}
                     pageSize={pageSize}
                     totalPages={totalPages}
                     onProjectChange={onProjectChange}
                     onPageChange={setCurrentPage}
                     onPageSizeChange={onPageSizeChange}
                     onRetryFiles={() => void refetch()}
                     /></>
}
export default FileUploadComponent;
